/**
 * SourceTicketCard — Linked PSA ticket display for a Security incident.
 *
 * Shown in the incident sidebar when the incident was escalated from a PSA ticket.
 * Displays ticket number and a link back to PSA.
 */

import { ExternalLink, Ticket } from 'lucide-react';

interface SourceTicketCardProps {
  ticketId: string;
  ticketNumber?: string;
  ticketUrl?: string;
  companyName?: string;
  priority?: string;
  status?: string;
}

const PRIORITY_BADGE: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

const STATUS_BADGE: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  open: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  pending: 'bg-gray-100 text-gray-600',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};

export function SourceTicketCard({
  ticketId,
  ticketNumber,
  ticketUrl,
  companyName,
  priority,
  status,
}: SourceTicketCardProps) {
  const displayId = ticketNumber ?? ticketId.slice(0, 8);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Ticket className="h-4 w-4 text-indigo-500" />
        Source PSA Ticket
      </h2>

      <div className="space-y-3">
        {/* Ticket number */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Ticket</span>
          <span className="text-sm font-mono font-medium text-gray-900">{displayId}</span>
        </div>

        {/* Company */}
        {companyName && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Customer</span>
            <span className="text-sm text-gray-900">{companyName}</span>
          </div>
        )}

        {/* Priority */}
        {priority && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Priority</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${PRIORITY_BADGE[priority] ?? 'bg-gray-100 text-gray-600'}`}>
              {priority}
            </span>
          </div>
        )}

        {/* Status */}
        {status && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Status</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[status] ?? 'bg-gray-100 text-gray-600'}`}>
              {status.replace(/_/g, ' ')}
            </span>
          </div>
        )}

        {/* Open in PSA link */}
        {ticketUrl ? (
          <a
            href={ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full mt-2 px-3 py-2 text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-200 hover:border-indigo-400 rounded-md transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in PSA
          </a>
        ) : (
          <p className="text-xs text-gray-400 text-center mt-2">
            Ticket ID: <span className="font-mono">{ticketId}</span>
          </p>
        )}
      </div>
    </div>
  );
}
